import React from 'react';
import { Database } from 'lucide-react';
import GenericTabs from '../../../../ui-components/Tabs/GenericTabs';
import { ClusterMemberCpr, formatClusterRole, isClusterMemberUp, memberMatchesSelection } from '../helpers/bgpVisibilityApi';

interface ClusterSelectorProps {
  reflectors: Array<{ id: string; display_name: string }>;
  selectedReflector: string | null;
  onReflectorChange: (reflectorId: string) => void;
  /** All cluster members (CTX-25 contract) — source of each member's HA role + realtime status. */
  members?: ClusterMemberCpr[];
}

/**
 * Matches a sidebar reflector to its `/visibility` member across the subId/name id-spaces
 * (see `memberMatchesSelection`): the sidebar keys reflectors by subId + display name, while
 * the visibility payload may key members by either, depending on the backend version.
 */
const findMember = (
  members: ClusterMemberCpr[] | undefined,
  reflector: { id: string; display_name: string }
): ClusterMemberCpr | undefined =>
  members?.find((m) =>
    memberMatchesSelection(
      { id: m.reflectorId, name: m.reflectorName },
      { id: reflector.id, name: reflector.display_name }
    )
  );

// Role → label goes through the shared, case-insensitive formatter so the pill, the table chips
// and the down-overlay copy can never disagree about a member's role.
const roleLabel = formatClusterRole;

/**
 * Floating cluster-member switch shown over the topology (a compact segmented control). Each tab
 * leads with the HA role ("Primary" / "Secondary") as the title, the reflector name as a subtitle,
 * and live up/down as a status pill. Falls back to the reflector name as the title when no role
 * is known; the status pill only appears once member data has loaded.
 */
export const ClusterSelector: React.FC<ClusterSelectorProps> = ({
  reflectors,
  selectedReflector,
  onReflectorChange,
  members
}) => {
  if (reflectors.length === 0) {
    return null;
  }

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[60] pointer-events-auto w-fit">
      <GenericTabs
        aria-label="Cluster member"
        tabs={reflectors.map((reflector) => {
          const member = findMember(members, reflector);
          const label = roleLabel(member?.role ?? null);
          const title = label ?? reflector.display_name;
          const subtitle = label ? reflector.display_name : null;
          const down = member ? !isClusterMemberUp(member) : false;
          return {
            label: (
              <span className="flex items-center gap-1.5 py-0.5">
                <Database className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="flex flex-col items-start gap-0.5">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[11px] font-semibold leading-none">{title}</span>
                    {member && (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none ${
                          down
                            ? 'bg-semantic-bg-secondary-subtle text-semantic-text-tertiary'
                            : 'bg-semantic-bg-success-secondary text-semantic-text-success'
                        }`}
                      >
                        {down ? 'Down' : 'Online'}
                      </span>
                    )}
                  </span>
                  {subtitle && (
                    <span className="text-[10px] font-normal leading-none text-semantic-text-tertiary">
                      {subtitle}
                    </span>
                  )}
                </span>
              </span>
            ),
            value: reflector.id
          };
        })}
        activeTab={selectedReflector || ''}
        onTabChange={onReflectorChange}
        size="xs"
        fullWidth={false}
      />
    </div>
  );
};
